import { type MutableRefObject, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import type { TextChain } from '@/api/types/stowed-element'
import { stripFreezeFromClipboardSnapshot } from '@/components/app/editor/select-all-policy'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextBoxData } from '@/lib/text/types/text-box-data'
import {
  attachPastePanelAnchorToShapeSnapshot,
  lockedObjectBlocksCopy,
} from '@/lib/editor/clipboard-paste'
import {
  PARTIAL_CUT_TOAST_DURATION_MS,
  partialCutSkippedMessage,
  partitionSelectionForCut,
} from '@/lib/editor/cut-selection-policy'
import {
  expandShapeSnapshotsForClipboard,
  expandTextSnapshotsForClipboard,
} from '@/lib/groups/clipboard-group-policy'
import {
  notifyFrozenDeleteBlocked,
  notifyFrozenGroupChildDeleteBlocked,
} from '../handlers/frozen-delete-feedback'
import { computeDeleteGestureScope } from '../helpers/delete-gesture-scope'
import { selectionHasChildUnderFrozenGroup } from '../helpers/frozen-group-child'
import type { CenterCanvasConfig } from '../types/center-canvas-config'
import type { ClipboardEntry } from '../types/clipboard-entry'

const LOCKED_COPY_MESSAGE = 'Object Locked: Can’t copy a locked object'
const LOCKED_CUT_MESSAGE = 'Object locked: Cannot cut a locked object.'

/**
 * The canvas clipboard: its history, the selection snapshots copy/cut/delete/menus read, Copy and
 * Cut (cut skips protected linked frames, WMPCM-8663), and whether anything on it is pasteable.
 * Paste itself lives in `use-clipboard-paste`.
 */
export function useClipboardCommands({
  internalShapeManagerRef,
  linkedTextChainsRef,
  unifiedLayerIdsRef,
  activeIsolationGroupId,
  pulseSelectionGeometryBlocked,
  onDeleteGestureScope,
  onDeleteShapesHandled,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  linkedTextChainsRef: MutableRefObject<TextChain[] | undefined>
  unifiedLayerIdsRef: MutableRefObject<string[]>
  activeIsolationGroupId: string | null
  pulseSelectionGeometryBlocked: () => void
  onDeleteGestureScope: CenterCanvasConfig['onDeleteGestureScope']
  onDeleteShapesHandled: CenterCanvasConfig['onDeleteShapesHandled']
}) {
  const clipboardHistoryRef = useRef<ClipboardEntry[]>([])

  const getCurrentSelectionSnapshots = useCallback(() => {
    const mgr = internalShapeManagerRef.current
    const selectedShapeIds = mgr?.getSelectedShapeIds() ?? []
    const allShapes = mgr?.getShapes() ?? []
    const shapeSnapshots = selectedShapeIds
      .map((id) => allShapes.find((shape) => shape.id === id))
      .filter((shape): shape is ShapeData => shape != null)
    const textSnapshots: TextBoxData[] = []

    return { shapeSnapshots, textSnapshots }
  }, [internalShapeManagerRef])

  const buildDeleteGestureScope = useCallback(
    (
      shapeSnapshots: readonly { id: string }[],
      textSnapshots: readonly { id: string }[]
    ): { txnId: string; ids: string[] } => computeDeleteGestureScope(shapeSnapshots, textSnapshots),
    []
  )

  const canUseClipboardEntry = useCallback(
    (entry: { sourceObjectIds: string[] }) => {
      const mgr = internalShapeManagerRef.current
      const shapes = mgr?.getShapes() ?? []

      // Mirrors `copySelectionToClipboard`'s gate: a locked *linked* frame stays pasteable, so a copy
      // taken from one isn't stranded (WMPCM-7012 AC3). The snapshot is already detached from the live
      // object and paste forces `locked: false`, so consulting the source's current lock state is only
      // a policy check here, not a correctness one.
      const chainsForCopy = linkedTextChainsRef.current

      for (const id of entry.sourceObjectIds) {
        const shape = shapes.find((candidate) => candidate.id === id)

        if (shape && lockedObjectBlocksCopy(shape, chainsForCopy)) return false
      }

      return true
    },
    [internalShapeManagerRef, linkedTextChainsRef]
  )

  const hasPasteCandidate = useCallback(() => {
    return clipboardHistoryRef.current.some((entry) => canUseClipboardEntry(entry))
  }, [canUseClipboardEntry])

  const copySelectionToClipboard = useCallback(
    (restrict?: { shapeIds: readonly string[]; textIds: readonly string[] }): boolean => {
      const mgr = internalShapeManagerRef.current
      const { shapeSnapshots: selectedShapes, textSnapshots: selectedText } =
        getCurrentSelectionSnapshots()

      const allShapes = mgr?.getShapes() ?? []
      // No second store to read since text-unification — a Text-tool frame is a shape, which is
      // also why `getCurrentSelectionSnapshots` returns an always-empty `textSnapshots`. Kept as a
      // named empty so the clipboard helpers below keep their shape/text-pair signature.
      const allText: TextBoxData[] = []
      // Cut passes `restrict` so a frame it was not allowed to remove never lands on the clipboard
      // (WMPCM-8663 TC-06). Copy always takes the whole selection.
      const sourceShapeIds = restrict ? restrict.shapeIds : selectedShapes.map((shape) => shape.id)
      const sourceTextIds = restrict ? restrict.textIds : selectedText.map((box) => box.id)
      const shapeSnapshots = expandShapeSnapshotsForClipboard([...sourceShapeIds], allShapes)
      const textSnapshots = expandTextSnapshotsForClipboard({
        shapeSnapshots,
        selectedTextIds: [...sourceTextIds],
        allTextBoxes: allText,
        allShapes,
      })

      if (shapeSnapshots.length === 0 && textSnapshots.length === 0) return false

      const chainsForCopy = linkedTextChainsRef.current

      if (
        shapeSnapshots.some((shape) => lockedObjectBlocksCopy(shape, chainsForCopy)) ||
        textSnapshots.some((box) => lockedObjectBlocksCopy(box, chainsForCopy))
      ) {
        toast.error(LOCKED_COPY_MESSAGE)
        return false
      }

      const sourceObjectIds = [
        ...shapeSnapshots.map((shape) => shape.id),
        ...textSnapshots.map((text) => text.id),
      ]
      const sourceObjectIdSet = new Set(sourceObjectIds)
      // Restore the same relative stacking order on paste — paste's creation loop doesn't create
      // shapes in z-order (groups must be created after their descendants; see
      // pasteFromClipboard in `use-clipboard-paste`), so paste can't rely on creation order for
      // this the way a single new shape does.
      const sourceZOrder = unifiedLayerIdsRef.current.filter((id) => sourceObjectIdSet.has(id))
      const entry = {
        shapeSnapshots: shapeSnapshots.map((shape) =>
          attachPastePanelAnchorToShapeSnapshot(stripFreezeFromClipboardSnapshot(shape))
        ),
        textSnapshots: textSnapshots.map((text) => stripFreezeFromClipboardSnapshot(text)),
        sourceObjectIds,
        sourceZOrder,
      }

      clipboardHistoryRef.current = [entry, ...clipboardHistoryRef.current].slice(0, 50)
      return true
    },
    [getCurrentSelectionSnapshots, internalShapeManagerRef, linkedTextChainsRef, unifiedLayerIdsRef]
  )

  const cutSelectionToClipboard = useCallback((): boolean => {
    const { shapeSnapshots, textSnapshots } = getCurrentSelectionSnapshots()

    // Cut used to abort outright when anything in the selection was locked or frozen, which left a
    // mixed chain entirely uncuttable. It now skips protected *linked* frames and cuts the rest
    // (WMPCM-8663); an unlinked protected object keeps the historical all-or-nothing block, so
    // plain shape/text cut behavior is unchanged.
    const partition = partitionSelectionForCut({
      shapes: shapeSnapshots,
      textBoxes: textSnapshots,
      chains: linkedTextChainsRef.current,
    })

    if (partition.kind === 'empty') return false

    if (partition.kind === 'blocked') {
      if (partition.reason === 'locked') {
        toast.error(LOCKED_CUT_MESSAGE)
      } else {
        notifyFrozenDeleteBlocked(pulseSelectionGeometryBlocked)
      }

      return false
    }

    // Isolation-mode parity with Delete: a child inside a frozen group is not cuttable either.
    if (activeIsolationGroupId) {
      const childUnderFrozenGroup = selectionHasChildUnderFrozenGroup(
        partition.cuttableShapes,
        partition.cuttableText,
        internalShapeManagerRef.current?.getShapes() ?? []
      )

      if (childUnderFrozenGroup) {
        notifyFrozenGroupChildDeleteBlocked(pulseSelectionGeometryBlocked)
        return false
      }
    }

    // Deterministic txnId (same scheme as deleteSelectedObjects) so the delete, any chain
    // relink, and its reflow group into one undo step instead of splitting into several
    // (WMPCM-6860 — cutting a linked frame previously broke undo-grouping). Derived from the
    // *cuttable* set rather than the raw selection, so the scope `repairChainAfterFrameDeleted`
    // reads never lists a skipped frame as co-deleted (WMPCM-8663 TC-02/TC-11).
    const { txnId: cutTxnId, ids: cutIds } = buildDeleteGestureScope(
      partition.cuttableShapes,
      partition.cuttableText
    )

    const cuttableShapeIds = partition.cuttableShapes.map((shape) => shape.id)
    // Only pass a restriction when something was actually skipped, so an ordinary cut takes the
    // exact same path through the manager as before. One id list now that a Text-tool frame is a
    // shape — `partition.cuttableText` is always empty here for the same reason.
    const restrictShapeIds = partition.skipped > 0 ? cuttableShapeIds : undefined

    // Snapshot only the frames that are really going, so a skipped one never reaches the clipboard
    // (TC-06). The selection itself is deliberately left alone — the managers are restricted by
    // argument instead, because `deleteSelectedShape`'s guards read selection from render state and
    // would not observe a same-tick `selectShapes`.
    const copied = copySelectionToClipboard(
      partition.skipped > 0 ? { shapeIds: cuttableShapeIds, textIds: [] } : undefined
    )

    if (!copied) return false

    // Before the managers remove anything — Cut reaches the same per-element delete handlers, and
    // hence the same chain repair, as Delete does.
    onDeleteGestureScope?.({ txnId: cutTxnId, ids: cutIds })

    // `restrictToIds` narrows the delete to the frames Cut was allowed to remove, leaving
    // protected chain members selected (WMPCM-8663). The TextManager half of this call is gone
    // with TextManager itself — a Text-tool frame is a shape, so one call covers both.
    internalShapeManagerRef.current?.deleteSelectedShape({
      historyTxnId: cutTxnId,
      restrictToIds: restrictShapeIds,
    })
    onDeleteShapesHandled?.()

    if (partition.skipped > 0) {
      toast.info(partialCutSkippedMessage(partition.skipped), {
        duration: PARTIAL_CUT_TOAST_DURATION_MS,
      })
    }

    return true
  }, [
    activeIsolationGroupId,
    buildDeleteGestureScope,
    copySelectionToClipboard,
    getCurrentSelectionSnapshots,
    onDeleteGestureScope,
    onDeleteShapesHandled,
    pulseSelectionGeometryBlocked,
    internalShapeManagerRef,
    linkedTextChainsRef,
  ])

  return {
    clipboardHistoryRef,
    getCurrentSelectionSnapshots,
    buildDeleteGestureScope,
    canUseClipboardEntry,
    hasPasteCandidate,
    copySelectionToClipboard,
    cutSelectionToClipboard,
  }
}
